import assert from 'assert';
import type { ReactElement } from 'react';
import type { NextPageWithLayout } from 'src/pages/_app';
import { useRouter } from 'next/router';
import { isNil } from 'es-toolkit';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { TaxEnrollmentBanner } from 'src/components/advisor-dashboard/account-overview/tax-enrollment-banner';
import AdvisorDashTopNavLayout from 'src/components/advisor-dashboard/layout/advisor-dash-top-nav-layout';
import { ClientHouseholdLayout } from 'src/components/advisor-dashboard/layout/client-household-layout';
import HouseholdTaxEnrollment from 'src/components/advisor-dashboard/taxes/household-tax-enrollment';
import { TaxHistory } from 'src/components/advisor-dashboard/taxes/tax-history/tax-history';
import { graphql } from 'src/gql';
import { withAdvisorGuard } from 'src/hocs/with-advisor-guard';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

export const HouseholdTaxesQuery = graphql(/* GraphQL */ `
  query HouseholdTaxes($salesforceAccountId: String!) {
    household(salesforceAccountId: $salesforceAccountId) {
      id
      taxEnrollmentBannerStatus
      ...HouseholdTaxes
    }
  }
`);

export const HouseholdTaxPageQuery = graphql(/* GraphQL */ `
  query HouseholdTaxPage($householdId: ID!) {
    householdTaxPage(householdId: $householdId) {
      primaryClient {
        ...HouseholdTaxHistory
      }
      coClient {
        ...HouseholdTaxHistory
      }
    }
  }
`);

function HouseholdTaxes() {
  const router = useRouter();
  const { salesforceAccountId } = router.query;
  assert(typeof salesforceAccountId === 'string', `Invalid salesforceAccountId: expected string`);

  const { data, loading } = useQueryWithErrorLogging(HouseholdTaxesQuery, {
    variables: { salesforceAccountId },
  });

  const householdId = data?.household.id;

  const { data: taxPageData } = useQueryWithErrorLogging(HouseholdTaxPageQuery, {
    variables: { householdId: householdId ?? '' },
    skip: !householdId,
  });

  if (!router.isReady) {
    return null;
  }

  const household = data?.household;
  if (isNil(household)) {
    return null;
  }

  const primaryClientData = taxPageData?.householdTaxPage.primaryClient;
  const coClientData = taxPageData?.householdTaxPage.coClient;
  const taxBannerStatus = data?.household.taxEnrollmentBannerStatus;

  return (
    <div className="flex flex-col gap-8">
      {taxBannerStatus && <TaxEnrollmentBanner bannerStatus={taxBannerStatus} />}
      <HouseholdTaxEnrollment householdFrag={household} loading={loading} />
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold">Household tax history</h2>
        <TaxHistory primaryClient={primaryClientData} coClient={coClientData} />
      </div>
    </div>
  );
}

const HouseholdTaxesPage: NextPageWithLayout = withLDConsumer()(withAdvisorGuard(HouseholdTaxes));

export default HouseholdTaxesPage;
HouseholdTaxesPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <AdvisorDashTopNavLayout>
      <ClientHouseholdLayout>{page}</ClientHouseholdLayout>
    </AdvisorDashTopNavLayout>
  );
};
